import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import { PGIC_SYSTEM } from '../codeSystem/pgic';
import { SURVEY_SCORES_SYSTEM } from '../codeSystem/surveyScores';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  ORDINAL_VALUE_URL,
  OPTION_PREFIX_URL,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  withEs,
} from './scoredSurvey';

/**
 * PGIC (Patient Global Impression of Change) — a single-item, patient-rated 1–7 scale of overall
 * change since treatment began (1 = "Very much improved" → 7 = "Very much worse"). It is required by
 * CMS ACCESS (MSK track) beneficiaries, where success is submission of the measure, not a score
 * threshold. PGIC has no LOINC codes, so — like the Daily Mood Scale ({@link ./moodScale}) — its
 * answer options and total-score result code live on minted in-house CodeSystems (`pgic`,
 * `survey-scores`) rather than LOINC. The selected ordinal (1–7) *is* the score — a single-term
 * `$extract` sum. Behavior is verified by `src/__tests__/integration/pgicExtractScoring.test.ts`.
 *
 * Source: the PGIC FHIR survey JSON attached to the originating issue (English + Spanish). The
 * English item text and answer displays are transcribed from the English source; the inline Spanish
 * translations on the `_`-sibling elements are transcribed from the Spanish source. Per the Spanish
 * source, that translation (by the National Library of Medicine) has not been independently
 * validated, so the bilingual wording must be reviewed before go-live and never free-translated.
 */

/** The minted total-score code carried by the result Observation / Goal / Task (on `survey-scores`). */
export const PGIC_RESULT_CODE = 'pgic';

const pgicAnswerOption = (
  ordinal: number,
  code: string,
  display: string,
  displayEs: string,
): QuestionnaireItemAnswerOptionWithTranslations => ({
  extension: [
    { url: OPTION_PREFIX_URL, valueString: String(ordinal) },
    { url: ORDINAL_VALUE_URL, valueDecimal: ordinal },
  ],
  valueCoding: { system: PGIC_SYSTEM, code, display, _display: withEs(displayEs) },
});

const pgicAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  pgicAnswerOption(1, 'very-much-improved', 'Very much improved', 'mejorado bastante'),
  pgicAnswerOption(2, 'much-improved', 'Much improved', 'mejorado mucho'),
  pgicAnswerOption(3, 'minimally-improved', 'Minimally improved', 'mejorado un poco'),
  pgicAnswerOption(4, 'no-change', 'No change', 'no ha cambiado'),
  pgicAnswerOption(5, 'minimally-worse', 'Minimally worse', 'empeorado un poco'),
  pgicAnswerOption(6, 'much-worse', 'Much worse', 'empeorado mucho'),
  pgicAnswerOption(7, 'very-much-worse', 'Very much worse', 'empeorado bastante'),
];

const pgicItem: QuestionnaireItemWithTranslations = {
  linkId: '/pgic',
  text: 'Since the start of the study, my overall status is:',
  _text: withEs('Desde que comenzó el estudio (el tratamiento), el dolor en general me ha…'),
  type: 'choice',
  required: true,
  answerOption: pgicAnswerOptions,
};

const pgicScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'pgic-score-tmpl',
  totalScoreCode: PGIC_RESULT_CODE,
  totalScoreSystem: SURVEY_SCORES_SYSTEM,
  display: 'PGIC',
  scoredItems: [pgicItem],
});

const URL = 'https://liza.health/fhir/questionnaire/pgic';

export const pgicQuestionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('pgic-score-tmpl')],
  contained: [pgicScoreObservationTemplate],
  url: URL,
  // No standard OID — mirror the canonical url as a FHIR identifier so re-deploys upsert
  // (identifier-based idempotency) rather than duplicate.
  identifier: [{ system: 'urn:ietf:rfc:3986', value: URL }],
  version: '1',
  name: 'PGIC',
  language: 'en',
  title: 'PGIC',
  _title: withEs('Impresión global de cambio del paciente (PGIC)'),
  status: 'active',
  subjectType: ['Patient'],
  date: '2026-06-26',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  copyright:
    'PGIC is a widely used clinical outcome measure in the public domain. No permission required to reproduce or distribute.',
  item: [pgicItem],
};
